import {
  Breakpoints,
  Link,
  TextAppearance,
  TextColor,
  TextCustom,
  TextSize,
  TextTag,
  TextWeight,
  useIsMobile,
} from '@vw-marketing/us-components';
import React from 'react';
import {useTranslations} from '../../hooks/use-translations';
import {useZipData} from '../../hooks/use-zip-data';
import {Dealer} from '../../typing/used-inventory';
import DealerDetails from '../dealer-details';
import Reload from '../icons/reload';
import {SimilarVehicles} from '../similar-vehicles';
import {userFilters} from '../../store/local-storage';
import {
  StyledNoResultsSectionContainer,
  StyledDealerHeader,
  StyledNoResultsHeader,
  StyledRefineLink,
} from './styles';

interface SearchNoResultsSectionProps {
  closestDealer?: Dealer;
  onHandleDealerContact: () => void;
  priceDisclaimer?: string;
  getWarrantyDisclaimer?: (year?: string) => string | undefined;
}

export const SearchNoResultsSection: React.FC<SearchNoResultsSectionProps> = (
  props,
) => {
  const {zipData} = useZipData();
  const {closestDealer, priceDisclaimer, getWarrantyDisclaimer, onHandleDealerContact} = props;
  const vehicle = {dealer: closestDealer};
  const isMobile = useIsMobile(Breakpoints.b560);
  const translations = useTranslations().resultsSection.noResults;

  const dealerHeaderMarkup = (
    <StyledDealerHeader className="no-results-dealer-header">
      <TextCustom
        appearance={TextAppearance.headline200}
        tag={TextTag.div}
        style={{
          [Breakpoints.default]: {
            fontWeight: TextWeight.bold,
            color: TextColor.inherit,
          },
          [Breakpoints.b1280]: {
            fontSize: TextSize.TextSize24,
          },
        }}
      >
        {translations.moreInfoTitle}
      </TextCustom>
      <TextCustom
        appearance={TextAppearance.copy100}
        tag={TextTag.div}
        style={{
          [Breakpoints.default]: {
            fontWeight: TextWeight.regular,
            color: TextColor.inherit,
          },
          [Breakpoints.b1280]: {
            fontSize: TextSize.TextSize16,
          },
        }}
      >
        {translations.findMoreInfo}
      </TextCustom>
    </StyledDealerHeader>
  );

  const noResultsHeaderMarkup = (
    <StyledNoResultsHeader id="no-results-header">
      <TextCustom
        appearance={TextAppearance.headline300}
        tag={TextTag.h1}
        style={{
          [Breakpoints.default]: {
            fontWeight: TextWeight.bold,
            color: TextColor.inherit,
            fontSize: TextSize.TextSize28,
          },
          [Breakpoints.b560]: {
            fontSize: TextSize.TextSize32,
          },
          [Breakpoints.b1280]: {
            fontSize: TextSize.TextSize44,
          },
        }}
      >
        {translations.sorryTitle}
      </TextCustom>
      <TextCustom
        appearance={TextAppearance.copy100}
        tag={TextTag.h2}
        style={{
          [Breakpoints.default]: {
            fontWeight: TextWeight.regular,
            color: TextColor.inherit,
            fontSize: TextSize.TextSize16,
          },
          [Breakpoints.b1280]: {
            fontSize: TextSize.TextSize24,
          },
        }}
      >
        {translations.noMatchTitle}
      </TextCustom>
      <StyledRefineLink>
        <Link
          onClick={() => {
            highlightContainerHandler('search-filter_filters');
          }}
          isInline
        >
          <Reload iconSize={12} />
          <TextCustom
            appearance={TextAppearance.copy100}
            tag={TextTag.span}
            style={{
              [Breakpoints.default]: {
                fontWeight: TextWeight.bold,
                color: TextColor.inherit,
              },
            }}
          >
            {translations.refineFilters}
          </TextCustom>
        </Link>
      </StyledRefineLink>
    </StyledNoResultsHeader>
  );

  /**
   * highlights a specific container and gray out the rest of
   * elements at the same level
   *
   * @param containerId
   * @returns void
   */
  const highlightContainerHandler = (containerId: string) => {
    const domRect = document
      .getElementById(containerId)
      ?.getBoundingClientRect();
    const highlightContainer = document.getElementById('highlight-container');
    if (domRect && highlightContainer) {
      highlightContainer.style.top = `${domRect.top}px`;
      highlightContainer.style.left = `${domRect.left}px`;
      highlightContainer.style.width = `${domRect.width}px`;
      highlightContainer.style.height = `${domRect.height}px`;
      highlightContainer.style.visibility = 'visible';
      highlightContainer.style.opacity = '1';

      setTimeout(() => {
        highlightContainer.style.visibility = 'hidden';
        highlightContainer.style.opacity = '0';
      }, 1000);
    }
  };

  return (
    <StyledNoResultsSectionContainer className="no-results-section">
      {noResultsHeaderMarkup}
      {!isMobile && dealerHeaderMarkup}
      <DealerDetails vehicle={vehicle} isPrimary onHandleContactDealer={onHandleDealerContact}>
        {isMobile && dealerHeaderMarkup}
      </DealerDetails>
      <SimilarVehicles
        modelNames={userFilters().filters?.modelName || []}
        zipcode={zipData.zip}
        priceDisclaimer={priceDisclaimer}
        getWarrantyDisclaimer={getWarrantyDisclaimer}
      />
      <div id="highlight-container" />
    </StyledNoResultsSectionContainer>
  );
};
